import { cn } from "@/lib/utils"
import { Info } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

interface DashboardDateRangePickerOption {
    label: string
    value: string
}

const DEFAULT_OPTIONS: readonly DashboardDateRangePickerOption[] = [
    { label: '1M', value: '1m' },
    { label: '3M', value: '3m' },
    { label: '6M', value: '6m' },
    { label: '12M', value: '12m' },
]

interface DashboardDateRangePickerProps {
    value: string
    onChange: (val: string) => void
    className?: string
    tooltipMessage?: string
    /** The pills to offer; the default is 1M, 3M, 6M and 12M (values '1m', '3m', '6m', '12m'). */
    options?: readonly DashboardDateRangePickerOption[]
}

export function DashboardDateRangePicker({
    value,
    onChange,
    className,
    tooltipMessage,
    options = DEFAULT_OPTIONS,
}: DashboardDateRangePickerProps) {
    return (
        <div className={cn("flex items-center gap-2", className)}>
            <div className="flex items-center bg-muted/40 p-0.5 rounded-full border border-border/50">
                {options.map((opt) => (
                    <button
                        key={opt.value}
                        onClick={() => onChange(opt.value)}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                            value === opt.value
                                ? 'bg-background shadow-sm text-foreground border border-border/50'
                                : 'text-muted-foreground hover:text-foreground border border-transparent'
                        }`}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>
            {tooltipMessage && (
                <TooltipProvider delayDuration={200}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Info className="h-3.5 w-3.5 text-muted-foreground/50 hover:text-muted-foreground transition-colors cursor-help shrink-0" />
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="font-normal leading-relaxed">
                            {tooltipMessage}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            )}
        </div>
    )
}
